/**
 * The create-booking body Book Ride sends from `createRide`.
 * Dispatch uses this same builder so a trip booked on either screen
 * differs only by the vehicleTypeId Dispatch also sends.
 */
export interface CreateRidePayloadInput {
  estimatedDistance: number;
  driverId: string;
  bookingType: number;
  directionPath: any;
  paymentMode: number;
  destinationAddresses: any[];
  bookingTime: number;
  vehiclePriceId: string;
  /** Price-row id is what create booking requires. Dispatch also sends the type id. */
  vehicleTypeId?: string;
  estimatedTime: number;
  isFixFare: boolean;
  pickupAddress: any;
  updatedCharges: any[];
  businessType: number;
  agencyId?: string;
  customerId?: string;
  customerDetail?: any;
  customerNote?: string;
  speakingLanguages?: any[];
  accessibilityIds?: any[];
  preferredGender?: string;
  /**
   * Promo the shown fare was priced with. The vehicle list auto-applies the best promo into
   * `priceDetail.total`, but create booking only honours a promo sent as `promoCodeId` — without
   * it the calltaker quoted the discounted fare and the rider was charged full price (#9582, #9597).
   */
  promoCodeId?: string;
}

export function buildCreateRidePayload(input: CreateRidePayloadInput): any {
  const params: any = {
    estimatedDistance: input.estimatedDistance,
    driverId: input.driverId,
    bookingType: input.bookingType,
    directionPath: input.directionPath,
    paymentMode: input.paymentMode,
    destinationAddresses: input.destinationAddresses,
    isBookForOther: false,
    bookingTime: input.bookingTime,
    vehiclePriceId: input.vehiclePriceId,
    isBidding: false,
    estimatedTime: input.estimatedTime,
    isFixFare: input.isFixFare,
    pickupAddress: input.pickupAddress,
    updatedCharges: input.updatedCharges,
    businessType: input.businessType
  };

  if (input.vehicleTypeId) params.vehicleTypeId = input.vehicleTypeId;
  if (input.agencyId) params.agencyId = input.agencyId;

  if (input.customerId) {
    params.customerId = input.customerId;
  } else {
    params.customerDetail = input.customerDetail;
    params.isBookForOther = true;
  }

  if (input.customerNote) params.customerNote = input.customerNote;
  params.speakingLanguages = input.speakingLanguages || [];
  params.accessibilityIds = input.accessibilityIds || [];
  if (input.preferredGender) params.preferredGender = input.preferredGender;
  if (input.promoCodeId) params.promoCodeId = input.promoCodeId;
  return params;
}

/**
 * Address object Book Ride builds in `_buildAddressObject` after Places details.
 * `longText` is the Places API (New) name for `long_name`.
 */
export function buildBookRideAddress(
  place: any,
  lat: number,
  lng: number,
  country: { name?: string; code2?: string } | null | undefined,
  isGoogle = true
): any {
  const address: any = {
    address: isGoogle ? place.formatted_address : place.place_name,
    latitude: lat,
    longitude: lng,
    country: country?.name || "",
    countryCode: country?.code2 || "",
    postalCode: "",
    placeId: place.placeId
  };

  if (isGoogle) {
    const components = place.address_components || place.addressComponents || [];
    address.postalCode = componentLongName(components, "postal_code");
    address.city = cityFromAddressComponents(components);
  } else {
    const context = place.context || [];
    const getContextValue = (idPart: string): string => context.find((c: any) => String(c.id || "").includes(idPart))?.text || "";
    address.postalCode = getContextValue("postcode");
    address.city = getContextValue("place") || getContextValue("locality") || getContextValue("district");
  }

  return address;
}

/** Places (New) uses `longText`. The older Geocoder uses `long_name`. */
function componentLongName(components: any[], type: string): string {
  const hit = (components || []).find((component) => (component?.types || []).includes(type));
  return hit?.longText || hit?.long_name || "";
}

/**
 * City is the locality. An airport often has no locality: use the county
 * (`administrative_area_level_3`, Leduc County) and never the province
 * (`administrative_area_level_1`, Alberta).
 */
export function cityFromAddressComponents(components: any[]): string {
  const rank = ["locality", "postal_town", "sublocality", "sublocality_level_1", "administrative_area_level_3"];
  for (const type of rank) {
    const name = componentLongName(components, type);
    if (name) return name;
  }
  return "";
}
